import dayjs from 'dayjs';
import * as React from 'react';
import { IXtimeRecommended, IXtimeMenuService, IXtimeMenuType } from '../../../API/GoMoto';
import { useAppointmentState, useAppointmentUpdateAndPersist } from '../../../context/Appointment/appointment';
import { Types, useCart, useCartDispatch } from '../../../context/cart';
import { useKioskState } from '../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../context/Localization/localization';
import { useHideKioskBackground } from '../../../context/Page/page';
import { CTAButton } from '../../../controls/ButtonBase';
import { GMDialogInfo } from '../../../controls/Dialog';
import { Heading } from '../../../controls/Heading/Heading';
import { SearchListResult } from '../../../controls/SearchListResults/SearchListResult/SearchListResult';
import { Spinner } from '../../../controls/Spinner/Spinner';
import { Table } from '../../../controls/Table';
import { formatAsCurrency } from '../../../helpers/Number';
import { ScrollableBody } from '../../Layout/ScrollableBody/ScrollableBody';
import { Xtime } from './XTime';
import { default as css } from './XTime.module.scss';
import { GMAppointmentName } from '../../../controls/AppointmentName';
import { GMSizedIcon } from '../../../controls/Icon';
import { GMCheckbox } from '../../../controls/Checkbox';
import { GMTitle } from '../../../controls/Title';

export interface IProps {
    onProceed: () => void;
    onSkip: () => void;
}

export const XtimeRecommended: React.FC<IProps> = (props) => {
    useHideKioskBackground()();
    const Localization = useLocalization();
    const appointment = useAppointmentState();
    const setAppointment = useAppointmentUpdateAndPersist();
    const kiosk = useKioskState();
    const xtime = new Xtime(kiosk.key, kiosk.dealer.key);
    const cart = useCart();
    const cartDispatch = useCartDispatch();
    const [recommendedServices, setRecommendedServices] = React.useState<IXtimeRecommended>();
    const [showPackageDetails, setShowPackageDetails] = React.useState(false);
    const [hasError, setHasError] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    const mileage =
        (typeof appointment.mileage === 'string' ? parseInt(appointment.mileage, 10) : appointment.mileage) || estimateMileageOnYear(parseInt(appointment.year ?? '0'));

    React.useEffect(() => {
        if (!loading && !recommendedServices && !hasError) {
            getRecommendedServices();
        }
    });

    const getRecommendedServices = async () => {
        setLoading(true);
        const recommended = await xtime.getRecommendedServices(appointment.vin, appointment.year ?? '', appointment.make ?? '', mileage);
        if (!recommended) {
            setHasError(true);
            props.onSkip();

            return;
        }

        setRecommendedServices(recommended);
        setLoading(false);
    };

    const toggleItem = async (item: IXtimeMenuType) => {
        const menuType = cart.xtime.menus.findIndex((menu) => menu.id === item.id) === -1 ? Types.AddXtimeMenu : Types.RemoveXtimeMenu;
        cartDispatch({ type: menuType,
            payload: item });
    };

    const toggleServiceItem = async ({ service, menuType }: { service: IXtimeMenuService; menuType: IXtimeMenuType; }) => {
        // If the menu type is not already added, we have to add it first.
        if (cart.xtime.menus.findIndex((menu) => menu.id === menuType.id) === -1) {
            await toggleItem(menuType);
        }

        cartDispatch({
            type: Types.ToggleXtimeService,
            payload: {
                id: service.id,
                goMotoId: service.goMotoId,
                type: 'menu',
                menuId: menuType.id,
                opCode: service.dmsOpcode,
                price: service.price,
                isCommonRepair: false,
                name: service.name,
                description: service.description,
            },
        });
    };

    const togglePackageDetails = () => {
        setShowPackageDetails(!showPackageDetails);
    };

    const onProceed = () => {
        const servicesRecommended = appointment.servicesRecommended || [];
        recommendedServices?.menuTypes.forEach((m) => m.services.forEach((s) => !servicesRecommended.includes(s.goMotoId) && servicesRecommended.push(s.goMotoId)));
        setAppointment({ ...appointment,
            xtimeServices: cart.xtime.services.map(({ type, menuId, ...rest }) => rest),
            servicesRecommended });
        props.onProceed();
    };

    if (!recommendedServices || loading) {
        return <Spinner />;
    }

    return (
        <ScrollableBody
            heading={
                <Heading
                    primaryHeading={(
                        <GMAppointmentName />
                    )}
                    secondaryHeading={Localization.TheseAreServicesWeRecommendAtYourCurrentMileageInterval}
                    leftAlign
                />
            }
            proceedButtons={
                <CTAButton
                    styleType={'primary'}
                    type={'button'}
                    onClick={onProceed}
                    title={Localization.ClickToProceedToNextStep}
                    label={Localization.Proceed}
                    id={Localization.ClickToProceedToNextStep}
                    ariaLabel={Localization.ClickToProceedToNextStep}
                />
            }
        >
            <div>
                <GMTitle headerLevel={4}>{Localization.formatString(Localization.RecommendedMaintenanceForMiles, recommendedServices.mileage)}</GMTitle>
                {recommendedServices.menuTypes.map((mt) => (
                    <XtimeItem
                        key={mt.id}
                        item={mt}
                        onToggle={toggleItem}
                        onTogglePackageDetails={togglePackageDetails}
                    />
                ))}
            </div>
            <GMDialogInfo
                ariaLabel={Localization.MaintenancePackageDetails}
                ariaDescribedBy={''}
                open={showPackageDetails && !!recommendedServices}
                title={Localization.MaintenancePackageDetails}
                onClose={() => setShowPackageDetails(false)}
            >
                <PackageDetails {...recommendedServices} onToggleService={toggleServiceItem} />
            </GMDialogInfo>
        </ScrollableBody>
    );
};

interface IXtimeItemProps {
    item: IXtimeMenuType;
    onToggle: (item: IXtimeMenuType) => Promise<void>;
    onTogglePackageDetails: () => void;
}

const XtimeItem: React.FC<IXtimeItemProps> = (props) => {
    const Localization = useLocalization();
    const [checked, setChecked] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    const cart = useCart();

    React.useEffect(() => {
        setChecked(cart.xtime.menus.findIndex((m) => m.id === props.item.id) > -1);
    }, [cart.xtime.menus, props.item]);

    const onToggle = async () => {
        setLoading(true);
        await props.onToggle(props.item);
        setLoading(false);
    };

    const generateDescription = (menuType: IXtimeMenuType) => {
        let description = '';
        const baseServices = menuType.services
            .filter((s) => !s.selectable)
            .slice(0, 3)
            .map((s) => s.name);
        description = baseServices.join(' | ');
        const optionalServices = menuType.services.filter((s) => s.selectable).length;

        if (optionalServices) {
            description = description + Localization.formatString(Localization.AndAdditionalServices, optionalServices);
        }
        else if (menuType.services.length > baseServices.length) {
            description = description + Localization.formatString(Localization.AndAdditionalServices, menuType.services.length - baseServices.length);
        }

        return description;
    };

    const toggleDetails = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        props.onTogglePackageDetails();
    };

    return (
        <div>
            <SearchListResult
                title={props.item.name}
                checked={checked}
                disabled={loading}
                onClick={onToggle}
            >
                <div>{generateDescription(props.item)}</div>
                {props.item.services.length && (
                    <div className={css.detailsButton}>
                        <div onClick={toggleDetails}>
                            <div className={css.detailsButton}>{Localization.PackageDetails}</div>
                        </div>
                    </div>
                )}
                <div>{formatAsCurrency(props.item.totalPrice, true)}</div>
            </SearchListResult>
        </div>
    );
};

const PackageDetails: React.FC<
    IXtimeRecommended & { onToggleService: ({ service, menuType }: { service: IXtimeMenuService; menuType: IXtimeMenuType; }) => Promise<void>; }
> = (props) => {
    const Localization = useLocalization();
    const cart = useCart();
    const services = Array.from(
        groupBy(
            props.menuTypes.flatMap((mt) => mt.services.sort((s) => s.order)
                .map((s) => ({ service: s,
                    menuType: mt }))),
            (s) => s.service.name,
        )
            .entries(),
    );

    const toggleItem = (item: { service: IXtimeMenuService; menuType: IXtimeMenuType; }) => {
        props.onToggleService(item);
    };

    const headings = ['', '', ...props.menuTypes.map((mt) => mt.name)];
    const rows = services.map(([name, services]) => [
        name,
        ...services.flatMap(({ service, menuType }) => [
            formatAsCurrency(service.price, true),
            (service.selectable && (
                <div role={'presentation'} className={css.checkboxWrapper}>
                    <GMCheckbox
                        iconSize={'medium'}
                        checked={cart.xtime.services.findIndex((s) => s.type === 'menu' && s.id === service.id) > -1}
                        onChange={() => toggleItem({ service,
                            menuType })}
                    />
                </div>
            )) || (
                <div className={css.includedIcon}>
                    <GMSizedIcon iconType={'Success'} ariaLabel={Localization.ServiceIncludedIcon} />
                    {Localization.Included}
                </div>
            ),
        ]),
    ]);

    return <Table headers={headings} rows={rows} />;
};

function groupBy<K, V>(list: Array<V>, keyGetter: (input: V) => K): Map<K, Array<V>> {
    const map = new Map<K, Array<V>>();
    /*
     * function groupBy(list, keyGetter) {
     * const map = new Map();
     */
    list.forEach((item) => {
        const key = keyGetter(item);
        const collection = map.get(key);
        if (!collection) {
            map.set(key, [item]);
        }
        else {
            collection.push(item);
        }
    });

    return map;
}

function estimateMileageOnYear(year: number) {
    // National average of miles driven applied over each year
    return (dayjs()
        .year() - year) * 13476;
}
